import {
  type ParseError,
  type ParseErrorCode,
  parseAlgebra,
} from "@/lib/tool-kit/algebra/parse";
import { type Poly, polyVariables } from "@/lib/tool-kit/algebra/poly";
import { type Uni, uniFromPoly } from "@/lib/tool-kit/algebra/uni";

/**
 * Helpers shared by the tools that read an equation or inequality from one text box:
 * splitting on the relation, parsing each side, and finding the single variable.
 */
export type StatementErrorCode =
  | ParseErrorCode
  | "no-relation"
  | "many-relations"
  | "many-variables"
  | "missing-side";

export interface StatementError {
  code: StatementErrorCode;
  token: string;
}

export function parseError(error: ParseError): StatementError {
  return { code: error.code, token: error.token };
}

export type SideResult =
  | { ok: true; polys: Poly[] }
  | { ok: false; error: StatementError };

/** Parse each side text; stops at the first that is wrong. */
export function parseSides(sides: readonly string[]): SideResult {
  const polys: Poly[] = [];
  for (const side of sides) {
    if (side.trim() === "") {
      return { ok: false, error: { code: "missing-side", token: "" } };
    }
    const result = parseAlgebra(side);
    if (!result.ok) {
      return { ok: false, error: parseError(result.error) };
    }
    polys.push(result.poly);
  }
  return { ok: true, polys };
}

export type VariableResult =
  | { ok: true; variable: string; parts: Uni[] }
  | { ok: false; error: StatementError };

/**
 * The one variable the statement uses (x when there is none) and each part as a
 * coefficient array in it. Two or more letters is an error, listed in the token.
 */
export function singleVariable(polys: readonly Poly[]): VariableResult {
  const names = new Set<string>();
  for (const poly of polys) {
    for (const name of polyVariables(poly)) {
      names.add(name);
    }
  }
  if (names.size > 1) {
    return {
      ok: false,
      error: { code: "many-variables", token: [...names].sort().join(", ") },
    };
  }
  const variable = [...names][0] ?? "x";
  const parts = polys.map((poly) => uniFromPoly(poly, variable) as Uni);
  return { ok: true, variable, parts };
}

export function countOf(text: string, character: string): number {
  return text.split(character).length - 1;
}
